import { CloseOutlined } from '@ant-design/icons';
import { Button, Spin, Tree } from 'antd';
import type { EventDataNode } from 'antd/es/tree';
import { CheckInfo } from 'rc-tree/lib/Tree';
import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { convertToDoorAddress, getUniqueValuesArray, handleResponse } from '../../../Helper';
import { graphicApi } from '../../../api';
import { useDraggable } from '../../../hooks/useDraggable';
import { AlarmDetail, AlarmModel, AlarmNote, AlarmState } from '../../../model/AlarmModel';
import { AlarmQueueDevice, AlarmQueueTreeNode, Coordinate, PendingItem, VelocityObject } from '../../../model/GraphicModel';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import { setOperatorNotesAdded } from '../../../store/alarm/actions';
import { selectOperatorNotesAdded } from '../../../store/alarm/selectors';
import { selectAckAlarmList, selectAlarmList, selectClearedAlarmList } from '../../../store/common/selectors';
import { setLastAlarmQueueCoordinatesAction, switchFloatingWindowVisibilityAction } from '../../../store/graphic/actions';
import { mapInfoSelector } from '../../../store/graphic/selectors';
import { AlarmsIcon } from '../../common';
import { proccessLiveAlarmEvent, updateTreeData } from '../helper';
import { AlarmActionButtons } from './AlarmActionButtons/AlarmActionButtons';
import { AlarmContent } from './AlarmContent/AlarmContent';
import { DeviceContent } from './DeviceContent/DeviceContent';
import styles from './alarmQueue.module.scss';
import { acknowledgeIcon, isSameAlarmQueueKey } from './helper';

type Props = {
	initialCoordinates: Coordinate;
	parentContainerId: string;
	parentCoordinates: Coordinate;
	// TODO: This array should be the plotted objects
	plottedDevices: Partial<VelocityObject>[];
};

type IndexedAlarm = AlarmQueueTreeNode & { childrenMap: Map<number, AlarmQueueTreeNode>; isLoadedInTree?: boolean; velocityObject?: Partial<VelocityObject> };

const alarmIconStyle: CSSProperties = { color: 'white', width: 16 };

const AlarmQueue: React.FC<Props> = ({ initialCoordinates, parentContainerId, parentCoordinates, plottedDevices }) => {
	const dispatch = useStoreDispatch();

	const [dataSource, setDataSource] = useState<Map<string, IndexedAlarm>>(new Map<string, IndexedAlarm>());
	const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [treeData, setTreeData] = useState<AlarmQueueTreeNode[]>([]);
	const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
	const [pendingIds, setPendingIds] = useState<PendingItem[]>([]);
	const [loadedKeys, setLoadedKeys] = useState<string[]>([]);

	const { Id } = useStoreSelector(mapInfoSelector);
	const operatorNotesAdded: AlarmNote[] = useStoreSelector(selectOperatorNotesAdded);
	const realTimeAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAlarmList);
	const realTimeAckAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAckAlarmList);
	const realTimeClearedAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectClearedAlarmList);

	useDraggable({
		parentContainerId,
		targetContainerId: `graphicAlarmQueue`,
		targetDragContainerId: `graphicAlarmQueueContent`,
		initialCoordinates,
		parentCoordinates,
		onDragEnd: (coordinate: Coordinate) => dispatch(setLastAlarmQueueCoordinatesAction(coordinate)),
	});

	useEffect(() => {
		batch(() => {
			setIsFirstLoad(true);
			setTreeData([]);
			setCheckedKeys([]);
			setDataSource(new Map<string, IndexedAlarm>());
			setPendingIds([]);
		});
		graphicApi
			.getAlarmQueueDevices([Id])
			.then(response => {
				if (!handleResponse(response)) {
					const [lTreeData, lIndexedAlarms] = createIndexedAlarms(response.Entity);
					batch(() => {
						setTreeData(lTreeData);
						setDataSource(lIndexedAlarms);
					});
				}
			})
			.finally(() => setIsFirstLoad(false));
	}, [Id]);

	useEffect(() => {
		if (realTimeAckAlarmList?.length > 0 || realTimeAlarmList?.length > 0 || realTimeClearedAlarmList?.length > 0) {
			const actualDataSourceCopy = new Map<string, IndexedAlarm>(dataSource);
			handleAlarms(realTimeAlarmList, actualDataSourceCopy);
			handleAckAlarms(realTimeAckAlarmList, actualDataSourceCopy);
			handleClearedAlarms(realTimeClearedAlarmList, actualDataSourceCopy);
			batch(() => {
				setDataSource(actualDataSourceCopy);
				setTreeData(convertFromMapToArray(actualDataSourceCopy));
			});
		}
	}, [realTimeAlarmList, realTimeAckAlarmList, realTimeClearedAlarmList]);

	useEffect(() => {
		if (operatorNotesAdded && operatorNotesAdded.length) {
			updateAlarmNotes(operatorNotesAdded);
			dispatch(setOperatorNotesAdded([]));
		}
	}, [operatorNotesAdded]);

	const createIndexedAlarms = (alarms: AlarmQueueDevice[]): [AlarmQueueTreeNode[], Map<string, IndexedAlarm>] => {
		const lIndexedAlarms = new Map<string, IndexedAlarm>();
		const lTreeData: AlarmQueueTreeNode[] = [];
		alarms.forEach(a => {
			const doorAddress: string = convertToDoorAddress(a.Address);
			const velocityObject: Partial<VelocityObject> = plottedDevices.find(vo => vo.Address === a.Address || vo.Address === doorAddress);
			lIndexedAlarms.set(a.Address, {
				...a,
				key: a.Address,
				title: <DeviceContent deviceInfo={a} />,
				childrenMap: new Map<number, AlarmQueueTreeNode>(),
				velocityObject,
			});
			lTreeData.push({ ...a, key: a.Address, title: <DeviceContent deviceInfo={a} /> });
		});
		return [lTreeData, lIndexedAlarms];
	};

	const getIndexedAlarm = (address: string, doorAddress: string, dataSource: Map<string, IndexedAlarm>): IndexedAlarm => {
		let idxAlarm = dataSource.get(address);
		if (idxAlarm) {
			return idxAlarm;
		}
		return dataSource.get(doorAddress);
	};

	const handleClearedAlarms = (alarmsToRemove: AlarmModel[], dataSource: Map<string, IndexedAlarm>) => {
		alarmsToRemove.forEach(atr => {
			const doorAddress: string = convertToDoorAddress(atr.Address);
			const velocityObject: Partial<VelocityObject> = plottedDevices.find(vo => vo.Address === atr.Address || vo.Address === doorAddress);
			if (velocityObject) {
				const idxAlarm = getIndexedAlarm(atr.Address, doorAddress, dataSource);
				if (idxAlarm) {
					idxAlarm.velocityObject = velocityObject;
					const isLoaded = loadedKeys.some(k => k === idxAlarm.key);
					idxAlarm.isLoadedInTree = isLoaded;

					if (atr.AlarmDetails?.length > 0) {
						atr.AlarmDetails.forEach(atrAd => {
							if (checkedKeys?.length > 0) {
								setCheckedKeys(prevState => {
									const clonedState = [...prevState];
									const checkedKeyIndex = clonedState.findIndex(x => isSameAlarmQueueKey(atrAd.ActiveAlarmId, x));
									if (checkedKeyIndex !== -1) {
										clonedState.splice(checkedKeyIndex, 1);
									}
									return clonedState;
								});
							}
							idxAlarm?.childrenMap?.delete(atrAd.ActiveAlarmId);
						});
					}

					if (atr.AlarmDetails?.length > 0) {
						idxAlarm.Count -= atr.AlarmDetails.length;
						idxAlarm.title = <DeviceContent deviceInfo={idxAlarm} />;
					}
					processLiveAlarm(idxAlarm);
					if (idxAlarm.Count === 0) {
						dataSource.delete(idxAlarm.Address);
						removeNodeKey(idxAlarm.Address);
					}
				}
			}
		});
	};

	const handleAlarms = (alarms: AlarmModel[], dataSource: Map<string, IndexedAlarm>) => {
		alarms.forEach(a => {
			const doorAddress: string = convertToDoorAddress(a.Address);
			const velocityObject: Partial<VelocityObject> = plottedDevices.find(vo => vo.Address === a.Address || vo.Address === doorAddress);
			if (velocityObject) {
				let idxAlarm = getIndexedAlarm(a.Address, doorAddress, dataSource);
				if (!idxAlarm) {
					idxAlarm = {
						...a,
						childrenMap: new Map<number, AlarmQueueTreeNode>(),
						Count: 0,
						key: a.Address,
						Name: velocityObject.DisplayText,
						ObjectId: velocityObject.ObjectId,
						ObjectType: velocityObject.ObjectType,
					};
					dataSource.set(idxAlarm.Address, idxAlarm);
				}
				idxAlarm.velocityObject = velocityObject;
				const isLoaded = loadedKeys.some(k => k === idxAlarm?.key);
				idxAlarm.isLoadedInTree = isLoaded;

				a.AlarmDetails.forEach(ad => {
					if (checkedKeys?.length > 0) {
						const checkedKeyIndex = checkedKeys.findIndex(x => x === a.Address || x === doorAddress);
						if (checkedKeyIndex !== -1) {
							setCheckedKeys(prevState => [...prevState, `${ad.ActiveAlarmId}_${AlarmState.Active}`]);
						}
					}

					idxAlarm.childrenMap.set(ad.ActiveAlarmId, {
						...a,
						key: `${ad.ActiveAlarmId}_${a.State}`,
						title: <AlarmContent data={a} />,
						isLeaf: true,
						selectable: false,
						switcherIcon: acknowledgeIcon(a.State),
					});
				});

				if (a.AlarmDetails?.length > 0) {
					idxAlarm.Count += a.AlarmDetails.length;
					idxAlarm.title = <DeviceContent deviceInfo={idxAlarm} />;
				}
			}
		});
	};

	const handleAckAlarms = (ackAlarms: AlarmModel[], dataSource: Map<string, IndexedAlarm>) => {
		ackAlarms.forEach(ackA => {
			const doorAddress: string = convertToDoorAddress(ackA.Address);
			const velocityObject: Partial<VelocityObject> = plottedDevices.find(vo => vo.Address === ackA.Address || vo.Address === doorAddress);
			if (velocityObject) {
				let idxAlarm = getIndexedAlarm(ackA.Address, doorAddress, dataSource);
				if (!idxAlarm) {
					idxAlarm = {
						...ackA,
						childrenMap: new Map<number, AlarmQueueTreeNode>(),
						Count: 0,
						key: ackA.Address,
						Name: velocityObject.DisplayText,
						ObjectId: velocityObject.ObjectId,
						ObjectType: velocityObject.ObjectType,
					};
					dataSource.set(idxAlarm.Address, idxAlarm);
				}
				idxAlarm.velocityObject = velocityObject;
				const isLoaded = loadedKeys.some(k => k === idxAlarm?.key);
				idxAlarm.isLoadedInTree = isLoaded;
				ackA.AlarmDetails.forEach(ackAd => {
					if (checkedKeys?.length > 0) {
						setCheckedKeys(prevState => {
							const clonedState = [...prevState];
							const checkedKeyIndex = clonedState.findIndex(x => isSameAlarmQueueKey(ackAd.ActiveAlarmId, x));
							if (checkedKeyIndex !== -1) {
								clonedState[checkedKeyIndex] = `${ackAd.ActiveAlarmId}_${AlarmState.Acknowledged}`;
							}

							return clonedState;
						});
					}
					const cAlarm = idxAlarm.childrenMap.get(ackAd.ActiveAlarmId);
					if (cAlarm) {
						cAlarm.key = `${ackAd.ActiveAlarmId}_${ackA.State}`;
						cAlarm.State = ackA.State;
						cAlarm.title = <AlarmContent data={ackA} />;
						cAlarm.switcherIcon = acknowledgeIcon(ackA.State);
					} else {
						idxAlarm.Count++;
						idxAlarm.title = <DeviceContent deviceInfo={idxAlarm} />;
						idxAlarm.childrenMap.set(ackAd.ActiveAlarmId, {
							...ackAd,
							isLeaf: true,
							key: `${ackAd.ActiveAlarmId}_${ackA.State}`,
							selectable: false,
							State: ackA.State,
							switcherIcon: acknowledgeIcon(ackA.State),
							title: <AlarmContent data={ackA} />,
						});
					}
				});
			}
		});
	};

	const convertFromMapToArray = (mapDataSource: Map<string, IndexedAlarm>): AlarmQueueTreeNode[] => {
		mapDataSource.forEach(v => {
			v.children = Array.from(v.childrenMap.values());
			processLiveAlarm(v);
		});
		return Array.from(mapDataSource.values());
	};

	const removeNodeKey = (key: string) => {
		setCheckedKeys(prevState => prevState.filter(x => x !== key));
		setPendingIds(prevState => prevState.filter(x => x.Address !== key));
	};

	const processLiveAlarm = (device: IndexedAlarm) => {
		const { isLoadedInTree, velocityObject, children: deviceAlarms } = device;
		velocityObject.HasAckAlarms = false;
		velocityObject.HasAlarms = false;
		if (velocityObject) {
			if (isLoadedInTree) {
				const deviceHasAlarms: boolean = device.Count > 0;
				if (deviceHasAlarms) {
					velocityObject.HasAlarms = deviceAlarms.some(a => a.State === 0);
					if (!velocityObject.HasAlarms) {
						velocityObject.HasAckAlarms = deviceAlarms.some(a => a.State === 1);
					}
				}
			}
			proccessLiveAlarmEvent({
				alarmAddress: velocityObject.Address,
				velocityObject,
				isVelocityObjectUpdated: isLoadedInTree,
			});
		}
	};

	const updateAlarmNotes = (operatorNotesAdded: AlarmNote[]) => {
		setTreeData(prevState => {
			const clonedPrevState = [...prevState];
			operatorNotesAdded.forEach((alarmNote: AlarmNote) => {
				clonedPrevState.forEach((node: AlarmQueueTreeNode) => {
					node.children?.forEach(alarm => {
						const titleElement: ReactElement = alarm.title as ReactElement;
						titleElement.props.data.AlarmDetails?.forEach((alarmDetails: AlarmDetail) => {
							if (alarmDetails.ActiveAlarmId === alarmNote.AlarmId) {
								alarmDetails.AlarmNotes = [...alarmDetails.AlarmNotes, alarmNote];
								alarm.title = <AlarmContent data={titleElement.props.data} />;
							}
						});
					});
				});
			});

			return clonedPrevState;
		});
	};

	const handleOnLoad = (data: EventDataNode<AlarmQueueTreeNode>) =>
		graphicApi
			.getAlarmQueueDeviceAlarms({ Address: data.Address, ObjectType: Number(data.ObjectType) })
			.then(response => {
				if (!handleResponse(response)) {
					const pendingIdsIndex: number = pendingIds.findIndex(x => x.Address === data.Address && x.ObjectType === data.ObjectType);
					if (pendingIdsIndex > -1) {
						const clonedPendingIds: PendingItem[] = [...pendingIds];
						checkPendingKeys(clonedPendingIds[pendingIdsIndex]);
						clonedPendingIds.splice(pendingIdsIndex, 1);
						setPendingIds(clonedPendingIds);
					}

					const childrenMap = new Map<number, AlarmQueueTreeNode>();
					const children: AlarmQueueTreeNode[] = response.Entity.map((x: AlarmModel) => {
						const alarmQueueTreeNode: AlarmQueueTreeNode = {
							...x,
							key: `${x.AlarmDetails[0].ActiveAlarmId}_${x.State}`,
							title: <AlarmContent data={x} />,
							isLeaf: true,
							selectable: false,
							switcherIcon: acknowledgeIcon(x.State),
						};
						childrenMap.set(x.AlarmDetails[0].ActiveAlarmId, alarmQueueTreeNode);
						return alarmQueueTreeNode;
					});

					const newDataSource = new Map<string, IndexedAlarm>(dataSource);
					const idxAlarm = newDataSource.get(data.Address);
					if (idxAlarm) {
						idxAlarm.childrenMap = childrenMap;
					}
					batch(() => {
						setDataSource(newDataSource);
						setTreeData(prevState => updateTreeData(prevState, data.key, children));
					});
				}
			})
			.finally(() => {
				if (!loadedKeys.some(k => k === data.key)) {
					const newLoadedKyes = [...loadedKeys, data.key];
					setLoadedKeys(newLoadedKyes);
				}
			});

	const handleOnCloseButton = () => {
		dispatch(
			switchFloatingWindowVisibilityAction({
				alarmQueue: false,
			})
		);
	};

	const checkPendingKeys = (pendingId: PendingItem) =>
		graphicApi.getAlarmQueueDeviceAlarmModels([pendingId], false).then(alarmsResponse => {
			if (!handleResponse(alarmsResponse)) {
				const clonedCheckedKeys = [...checkedKeys];
				setCheckedKeys(
					getUniqueValuesArray(
						clonedCheckedKeys,
						alarmsResponse.Entity.map(x => `${x.AlarmDetails[0].ActiveAlarmId}_${x.State}`)
					)
				);
			}
		});

	const onCheck = (checkedKeysValue: string[], info: CheckInfo<AlarmQueueTreeNode>) => {
		const { node } = info;

		if (!node.key.toString().includes('_') && (node.children === undefined || node.children.length === 0)) {
			const clonedPendingIds: Set<PendingItem> = new Set([...pendingIds]);
			const currentPendingNode: PendingItem = { Address: node.Address, ObjectType: node.ObjectType };

			if (node.checked) {
				clonedPendingIds.forEach(x => {
					if (x.Address === currentPendingNode.Address && x.ObjectType === currentPendingNode.ObjectType) {
						clonedPendingIds.delete(x);
					}
				});
			} else {
				clonedPendingIds.add(currentPendingNode);
			}

			setPendingIds(Array.from(clonedPendingIds));
		}

		setCheckedKeys(checkedKeysValue);
	};

	return (
		<div id='graphicAlarmQueue' className={styles.alarmQueue}>
			<div id='graphicAlarmQueueContainer' className={styles.alarmQueueContainer}>
				<div id='graphicAlarmQueueHeader' className={styles.alarmQueueHeader}>
					<div className={styles.devicesTitle}>
						<AlarmsIcon style={alarmIconStyle} />
						&nbsp;
						<span id='graphicAlarmQueueTitle' className={styles.bannerLabel}>
							{_('AlarmQueue')}
						</span>
					</div>
					<div>
						<Button
							id='graphicAlarmQueueCloseButton'
							onClick={handleOnCloseButton}
							className={styles.bannerButton}
							type='text'
							size='small'
							icon={<CloseOutlined className={styles.bannerCloseIcon} />}
						/>
					</div>
				</div>
				<div id='graphicAlarmQueueContent' className={styles.alarmQueueContent}>
					{isFirstLoad ? (
						<Spin tip={`${_('Loading')}...`} spinning={isFirstLoad} size='large' wrapperClassName={styles.spin}>
							<div></div>
						</Spin>
					) : (
						<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
							<AlarmActionButtons checkedKeys={checkedKeys} treeData={treeData} pendingIds={pendingIds} setIsLoading={setIsLoading} />
							<Tree
								className={styles.rowColor}
								checkable
								loadData={handleOnLoad}
								treeData={treeData}
								height={500}
								selectable={false}
								motion={false}
								onCheck={onCheck}
								checkedKeys={checkedKeys}
							/>
						</Spin>
					)}
				</div>
			</div>
		</div>
	);
};

export { AlarmQueue };
